// frontend/packages/business-console/src/components/help/HelpLink.jsx
//
// Small contextual help link that appears in editor sections.
// Links to a specific help article relevant to the current context.

import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Link, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

export default function HelpLink({ topic, label }) {
  const article = HELP_TOPICS[topic];
  const { slug } = useParams();
  if (!article || !slug) return null;

  return (
    <Link
      component={RouterLink}
      to={`/business/${slug}/help#${topic}`}
      underline="hover"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: 12,
        opacity: 0.6,
        "&:hover": { opacity: 1 },
      }}
    >
      <HelpOutlineIcon sx={{ fontSize: 14 }} />
      <Typography variant="caption">{label || article.linkText || "Need help?"}</Typography>
    </Link>
  );
}

export const HELP_TOPICS = {
  "edit-text": {
    title: "How to edit text on your website",
    linkText: "How do I edit this?",
    content: [
      "Click on any text field to start editing. Your changes are saved as a draft until you publish them.",
      "To make your changes live, click the green Save & Publish button. Your website will update within 1-2 minutes.",
      "If you make a mistake, click the three-dot menu (...) and choose Restore to published version.",
    ],
  },
  "change-logo": {
    title: "How to change your logo",
    linkText: "How do I change my logo?",
    content: [
      "Go to Settings in the sidebar, then click the Brand tab.",
      "Find the Logo field and click Upload to choose a new logo from your computer.",
      "For best results, use an SVG or PNG file with a transparent background.",
      "Click Save & Publish to make the change live.",
    ],
  },
  "add-blog-post": {
    title: "How to add a blog post",
    linkText: "How do I write a blog post?",
    content: [
      "Click + New next to Blog in the sidebar.",
      "Enter a name for your post (this becomes the web address, like my-first-post).",
      "Fill in the Title, Description, and Date fields.",
      "Write your post content in the Page Content area at the bottom.",
      "To add a photo, click Add Image and choose a file from your computer.",
      "Click Save & Publish when you are ready to go live.",
    ],
  },
  "upload-image": {
    title: "How to upload or change an image",
    linkText: "How do I change this image?",
    content: [
      "Find the image field you want to change (it shows a small preview thumbnail).",
      "Click Upload to choose a new file from your computer, or Browse to pick from your media library.",
      "The image will upload automatically and appear in the preview.",
      "Click Save & Publish to make the change live on your website.",
    ],
  },
  "update-contact": {
    title: "How to update your contact information",
    linkText: "How do I update my contact info?",
    content: [
      "Go to Settings in the sidebar, then click the Contact tab.",
      "Update your phone number, email, address, or business hours.",
      "Click Save & Publish to make the changes live.",
      "Your contact information appears on every page of your website (header, footer, and contact page).",
    ],
  },
  "add-team-member": {
    title: "How to add a team member",
    linkText: "How do I add someone to the team page?",
    content: [
      "Click + New next to Team in the sidebar.",
      "Enter their name as the filename (like jane-doe).",
      "Fill in their Name, Role, and a Short Bio.",
      "Upload a headshot photo using the Image field.",
      "Click Save & Publish to add them to your website.",
    ],
  },
  "add-testimonial": {
    title: "How to add a customer review",
    linkText: "How do I add a review?",
    content: [
      "Click + New next to Testimonials in the sidebar.",
      "Enter the customer name as the filename.",
      "Fill in their Name, the Quote (their review text), and a Rating (1-5).",
      "Optionally add their City, Company, or Role for context.",
      "Click Save & Publish to show the review on your website.",
    ],
  },
  "edit-hero": {
    title: "How to change your homepage hero section",
    linkText: "How do I change the big section at the top?",
    content: [
      "Go to Pages in the sidebar and click Home.",
      "Scroll down to the Hero section.",
      "Edit the Headline, Supporting Text, or Small Label Above Headline.",
      "To change the background image, find Background Image and click Upload.",
      "Click Save & Publish to make the changes live.",
    ],
  },
  "edit-sections": {
    title: "How to edit page sections",
    linkText: "How do sections work?",
    content: [
      "Sections are the building blocks of your pages. Each section has a type (like Feature Grid, Process Steps, or Call to Action).",
      "Click on a section to expand it and see its fields.",
      "You can reorder sections using the up/down arrows, or delete them with the trash icon.",
      "For adding new section types, contact the UpRyzze team.",
    ],
  },
  "save-publish": {
    title: "Saving and publishing changes",
    linkText: "What is the difference between Save Draft and Publish?",
    content: [
      "Save Draft saves your work without making it live. Use this to save progress and preview later.",
      "Save & Publish saves your work AND makes it live on your website. Changes appear within 1-2 minutes.",
      "You can always undo by clicking the three-dot menu (...) and choosing Restore to published version.",
    ],
  },
  "settings-overview": {
    title: "Understanding your site settings",
    linkText: "What are site settings?",
    content: [
      "Site settings control things that appear on every page of your website.",
      "Brand: Your logo, favicon, and default images.",
      "Contact: Phone, email, address, and business hours.",
      "Social Media: Links to your social profiles.",
      "Search Engines: How your site appears in Google.",
      "For changes to Tracking, Forms, Legal, or Features, contact the UpRyzze team.",
    ],
  },
};
